import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import * as L from 'leaflet';

// Import Warehouses interfaces
import {
  Warehouse,
  WarehouseCreateRequest,
  WarehouseUpdateRequest,
  WarehouseSearchFilters,
  WarehouseResponse,
  WarehouseStatistics,
  WarehouseStatus,
  WarehouseType,
  WarehouseAffiliateType
} from '../../interfaces/Warehouses.interfaces';

// Import RoutingService
import { RoutingService } from '../../services/routing.service';

// Interface corrispondente alla tabella Opr_Routings
interface RoutingLeg {
    routing_id: number;
    routing_code: string;
    routing_location_start_code: string;
    routing_location_start_type: string;
    routing_location_end_code: string;
    routing_location_end_type: string;
    routing_condition_type?: string;
    routing_condition?: string;
    routing_transport_mode: string;
    routing_vehicle_type: string;
    routing_priority_level: number;
    routing_estimated_time?: number;
    routing_distance_km?: number;
    routing_cost?: number;
    routing_sequence_number: number;
    routing_carrier_code?: string;
    routing_departure_time?: Date;
    routing_arrival_time?: Date;
    routing_valid_from?: Date;
    routing_valid_to?: Date;
    routing_description?: string;
    routing_version: number;
    routing_created_at: Date;
}

// Interface per le location utilizzate nei routing
interface RoutingLocation {
    code: string;
    name: string;
    type: string;
    lat: number;
    lng: number;
    address: string;
}

// Interface per i percorsi aggregati da visualizzare
interface RouteGroup {
    routing_code: string;
    name: string;
    description?: string;
    legs: RoutingLeg[];
    totalDistance: number;
    totalTime: number;
    totalCost: number;
    status: 'planned' | 'active' | 'completed' | 'cancelled';
    priority: number;
    transport_mode: string;
    vehicle_type: string;
    carrier_code?: string;
    start_location: RoutingLocation;
    end_location: RoutingLocation;
    intermediate_locations: RoutingLocation[];
}

@Component({
    selector: 'app-routing',
    standalone: true,
    imports: [CommonModule, FormsModule, TranslateModule],
    templateUrl: './routing.component.html',
    styleUrls: ['./routing.component.css']
})
export class RoutingComponent implements OnInit, OnDestroy, AfterViewInit {
    private map!: L.Map;
    private markers: L.Marker[] = [];
    private routeLines: L.Polyline[] = [];
    private warehouseMarkers: L.Marker[] = [];

    // Nuove proprietà per gestire routing legs
    routingLegs: RoutingLeg[] = [];
    routeGroups: RouteGroup[] = [];
    selectedRouteGroup: RouteGroup | null = null;
    locations: RoutingLocation[] = [];
    isLoading = false;
    showingWarehouses = false;

    // Search and filter properties
    searchTerm: string = '';
    statusFilter: string = '';
    filteredRouteGroups: RouteGroup[] = [];
    originalRouteGroups: RouteGroup[] = [];
    
    // Leg details expansion state
    legDetailsExpanded: boolean[] = [];

    // Icone personalizzate per i diversi tipi di punti
    private pickupIcon = L.divIcon({
        className: 'custom-marker pickup-marker',
        html: '<div class="marker-inner"><i class="fas fa-box"></i></div>',
        iconSize: [30, 30],
        iconAnchor: [15, 30]
    });

    private deliveryIcon = L.divIcon({
        className: 'custom-marker delivery-marker',
        html: '<div class="marker-inner"><i class="fas fa-truck"></i></div>',
        iconSize: [30, 30],
        iconAnchor: [15, 30]
    });

    private warehouseIcon = L.divIcon({
        className: 'custom-marker warehouse-marker',
        html: '<div class="marker-inner"><i class="fas fa-warehouse"></i></div>',
        iconSize: [30, 30],
        iconAnchor: [15, 30]
    });

    constructor(private routingService: RoutingService) { }

    ngOnInit(): void {
        this.loadRoutingLegs();
    }

    ngAfterViewInit(): void {
        this.initializeMap();
    }

    ngOnDestroy(): void {
        if (this.map) {
            this.map.remove();
        }
    }

    private initializeMap(): void {
        // Aspetta che il DOM sia pronto
        setTimeout(() => {
            const mapElement = document.getElementById('map');
            if (!mapElement) {
                console.warn('Map container not found, retrying...');
                // Riprova dopo un altro timeout
                setTimeout(() => this.initializeMap(), 100);
                return;
            }

            // Se la mappa esiste già, rimuovila
            if (this.map) {
                this.map.remove();
            }

            // Inizializza la mappa centrata sull'Italia
            this.map = L.map('map').setView([41.9028, 12.4964], 6);

            // Aggiungi tile layer
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '© OpenStreetMap contributors'
            }).addTo(this.map);

            // Forza il refresh della mappa dopo l'inizializzazione
            setTimeout(() => {
                if (this.map) {
                    this.map.invalidateSize();
                }
            }, 100);

            // Se ci sono route groups, mostra il primo
            if (this.routeGroups.length > 0) {
                this.selectRouteGroup(this.routeGroups[0]);
            }
        }, 100);
    }

    private loadRoutingLegs(): void {
        this.isLoading = true;

        // Simula il caricamento dei dati (sostituisci con chiamata API reale)
        setTimeout(() => {
            this.routingLegs = this.generateSampleRoutingLegs();
            this.locations = this.generateSampleLocations();
            this.routeGroups = this.groupRoutingLegsByCode();
            this.originalRouteGroups = [...this.routeGroups];
            this.filteredRouteGroups = [...this.routeGroups];
            this.isLoading = false;

            if (this.map && this.routeGroups.length > 0) {
                this.selectRouteGroup(this.routeGroups[0]);
            }
        }, 1000);
    }

    private generateSampleRoutingLegs(): RoutingLeg[] {
        return [
            // Rotta Milano - Roma (ROUTE_001)
            {
                routing_id: 1,
                routing_code: 'ROUTE_001',
                routing_location_start_code: 'WH_MILANO',
                routing_location_start_type: 'WAREHOUSE',
                routing_location_end_code: 'WH_BOLOGNA',
                routing_location_end_type: 'WAREHOUSE',
                routing_transport_mode: 'TERRESTRE',
                routing_vehicle_type: 'CAMION',
                routing_priority_level: 1,
                routing_estimated_time: 3,
                routing_distance_km: 210,
                routing_cost: 150,
                routing_sequence_number: 1,
                routing_carrier_code: 'CARRIER_001',
                routing_departure_time: new Date('2024-01-15T08:00:00'),
                routing_arrival_time: new Date('2024-01-15T11:00:00'),
                routing_description: 'Milano - Bologna',
                routing_version: 1,
                routing_created_at: new Date('2024-01-10T10:00:00')
            },
            {
                routing_id: 2,
                routing_code: 'ROUTE_001',
                routing_location_start_code: 'WH_BOLOGNA',
                routing_location_start_type: 'WAREHOUSE',
                routing_location_end_code: 'WH_FIRENZE',
                routing_location_end_type: 'WAREHOUSE',
                routing_transport_mode: 'TERRESTRE',
                routing_vehicle_type: 'CAMION',
                routing_priority_level: 1,
                routing_estimated_time: 2,
                routing_distance_km: 105,
                routing_cost: 80,
                routing_sequence_number: 2,
                routing_carrier_code: 'CARRIER_001',
                routing_departure_time: new Date('2024-01-15T11:30:00'),
                routing_arrival_time: new Date('2024-01-15T13:30:00'),
                routing_description: 'Bologna - Firenze',
                routing_version: 1,
                routing_created_at: new Date('2024-01-10T10:00:00')
            },
            {
                routing_id: 3,
                routing_code: 'ROUTE_001',
                routing_location_start_code: 'WH_FIRENZE',
                routing_location_start_type: 'WAREHOUSE',
                routing_location_end_code: 'WH_ROMA',
                routing_location_end_type: 'WAREHOUSE',
                routing_transport_mode: 'TERRESTRE',
                routing_vehicle_type: 'CAMION',
                routing_priority_level: 1,
                routing_estimated_time: 3,
                routing_distance_km: 257,
                routing_cost: 180,
                routing_sequence_number: 3,
                routing_carrier_code: 'CARRIER_001',
                routing_departure_time: new Date('2024-01-15T14:00:00'),
                routing_arrival_time: new Date('2024-01-15T17:00:00'),
                routing_description: 'Firenze - Roma',
                routing_version: 1,
                routing_created_at: new Date('2024-01-10T10:00:00')
            },
            // Rotta Locale Lombardia (ROUTE_002)
            {
                routing_id: 4,
                routing_code: 'ROUTE_002',
                routing_location_start_code: 'WH_BERGAMO',
                routing_location_start_type: 'WAREHOUSE',
                routing_location_end_code: 'WH_BRESCIA',
                routing_location_end_type: 'WAREHOUSE',
                routing_transport_mode: 'TERRESTRE',
                routing_vehicle_type: 'FURGONE',
                routing_priority_level: 2,
                routing_estimated_time: 1.5,
                routing_distance_km: 56,
                routing_cost: 45,
                routing_sequence_number: 1,
                routing_carrier_code: 'CARRIER_002',
                routing_departure_time: new Date('2024-01-16T09:00:00'),
                routing_arrival_time: new Date('2024-01-16T10:30:00'),
                routing_description: 'Bergamo - Brescia',
                routing_version: 1,
                routing_created_at: new Date('2024-01-11T09:00:00')
            },
            {
                routing_id: 5,
                routing_code: 'ROUTE_002',
                routing_location_start_code: 'WH_BRESCIA',
                routing_location_start_type: 'WAREHOUSE',
                routing_location_end_code: 'WH_CREMONA',
                routing_location_end_type: 'WAREHOUSE',
                routing_transport_mode: 'TERRESTRE',
                routing_vehicle_type: 'FURGONE',
                routing_priority_level: 2,
                routing_estimated_time: 2,
                routing_distance_km: 75,
                routing_cost: 55,
                routing_sequence_number: 2,
                routing_carrier_code: 'CARRIER_002',
                routing_departure_time: new Date('2024-01-16T11:00:00'),
                routing_arrival_time: new Date('2024-01-16T13:00:00'),
                routing_description: 'Brescia - Cremona',
                routing_version: 1,
                routing_created_at: new Date('2024-01-11T09:00:00')
            }
        ];
    }

    private generateSampleLocations(): RoutingLocation[] {
        return [
            {
                code: 'WH_MILANO',
                name: 'Magazzino Milano',
                type: 'WAREHOUSE',
                lat: 45.4642,
                lng: 9.1900,
                address: 'Via Milano 123, Milano'
            },
            {
                code: 'WH_BOLOGNA',
                name: 'Magazzino Bologna',
                type: 'WAREHOUSE',
                lat: 44.4949,
                lng: 11.3426,
                address: 'Via Bologna 45, Bologna'
            },
            {
                code: 'WH_FIRENZE',
                name: 'Magazzino Firenze',
                type: 'WAREHOUSE',
                lat: 43.7696,
                lng: 11.2558,
                address: 'Via Firenze 67, Firenze'
            },
            {
                code: 'WH_ROMA',
                name: 'Magazzino Roma',
                type: 'WAREHOUSE',
                lat: 41.9028,
                lng: 12.4964,
                address: 'Via Roma 89, Roma'
            },
            {
                code: 'WH_BERGAMO',
                name: 'Deposito Bergamo',
                type: 'WAREHOUSE',
                lat: 45.6983,
                lng: 9.6773,
                address: 'Via Bergamo 12, Bergamo'
            },
            {
                code: 'WH_BRESCIA',
                name: 'Magazzino Brescia',
                type: 'WAREHOUSE',
                lat: 45.5416,
                lng: 10.2118,
                address: 'Via Brescia 34, Brescia'
            },
            {
                code: 'WH_CREMONA',
                name: 'Hub Cremona',
                type: 'WAREHOUSE',
                lat: 45.1335,
                lng: 10.0422,
                address: 'Via Cremona 56, Cremona'
            }
        ];
    }

    private groupRoutingLegsByCode(): RouteGroup[] {
        const routeMap = new Map<string, RoutingLeg[]>();
        
        // Raggruppa le legs per routing_code
        this.routingLegs.forEach(leg => {
            if (!routeMap.has(leg.routing_code)) {
                routeMap.set(leg.routing_code, []);
            }
            routeMap.get(leg.routing_code)!.push(leg);
        });

        const routeGroups: RouteGroup[] = [];

        routeMap.forEach((legs, routingCode) => {
            // Ordina le legs per sequence_number
            legs.sort((a, b) => a.routing_sequence_number - b.routing_sequence_number);

            const firstLeg = legs[0];
            const lastLeg = legs[legs.length - 1];

            const startLocation = this.locations.find(loc => loc.code === firstLeg.routing_location_start_code);
            const endLocation = this.locations.find(loc => loc.code === lastLeg.routing_location_end_code);

            if (!startLocation || !endLocation) return;

            // Trova tutte le location intermedie
            const intermediateLocations: RoutingLocation[] = [];
            for (let i = 0; i < legs.length - 1; i++) {
                const intermediateLocation = this.locations.find(loc => loc.code === legs[i].routing_location_end_code);
                if (intermediateLocation) {
                    intermediateLocations.push(intermediateLocation);
                }
            }

            const totalDistance = legs.reduce((sum, leg) => sum + (leg.routing_distance_km || 0), 0);
            const totalTime = legs.reduce((sum, leg) => sum + (leg.routing_estimated_time || 0), 0);
            const totalCost = legs.reduce((sum, leg) => sum + (leg.routing_cost || 0), 0);

            const routeGroup: RouteGroup = {
                routing_code: routingCode,
                name: this.getRouteGroupName(routingCode, startLocation, endLocation),
                description: `${routingCode} - ${startLocation.name} → ${endLocation.name}`,
                legs: legs,
                totalDistance: totalDistance,
                totalTime: totalTime,
                totalCost: totalCost,
                status: this.determineRouteStatus(legs),
                priority: firstLeg.routing_priority_level,
                transport_mode: firstLeg.routing_transport_mode,
                vehicle_type: firstLeg.routing_vehicle_type,
                carrier_code: firstLeg.routing_carrier_code,
                start_location: startLocation,
                end_location: endLocation,
                intermediate_locations: intermediateLocations
            };

            routeGroups.push(routeGroup);
        });

        return routeGroups;
    }

    private getRouteGroupName(routingCode: string, startLocation: RoutingLocation, endLocation: RoutingLocation): string {
        return `${startLocation.name} → ${endLocation.name}`;
    }

    private determineRouteStatus(legs: RoutingLeg[]): 'planned' | 'active' | 'completed' | 'cancelled' {
        const now = new Date();
        const firstDeparture = legs[0].routing_departure_time;
        const lastArrival = legs[legs.length - 1].routing_arrival_time;

        if (!firstDeparture || !lastArrival) return 'planned';

        if (now < firstDeparture) return 'planned';
        if (now > lastArrival) return 'completed';
        return 'active';
    }

    selectRouteGroup(routeGroup: RouteGroup): void {
        this.selectedRouteGroup = routeGroup;
        
        // Inizializza l'array dei dettagli espansi (tutti chiusi di default)
        this.legDetailsExpanded = new Array(routeGroup.legs.length).fill(false);
        
        // Se la mappa non esiste ancora, inizializzala
        if (!this.map) {
            // Aspetta che il container della mappa sia presente nel DOM
            setTimeout(() => {
                this.initializeMap();
                // Dopo aver inizializzato la mappa, mostra il percorso
                setTimeout(() => {
                    if (this.map) {
                        this.displayRouteGroupOnMap(routeGroup);
                    }
                }, 200);
            }, 100);
        } else {
            this.displayRouteGroupOnMap(routeGroup);
        }
    }

    private displayRouteGroupOnMap(routeGroup: RouteGroup): void {
        if (!this.map) {
            console.warn('Map not initialized yet');
            return;
        }

        // Forza il refresh della mappa prima di aggiungere i marcatori
        setTimeout(() => {
            if (this.map) {
                this.map.invalidateSize();
            }
        }, 100);

        // Rimuovi markers e linee precedenti
        this.clearMap();

        // Crea un array di punti da visualizzare sulla mappa
        const mapPoints: Array<{location: RoutingLocation, sequence: number, type: string}> = [];
        
        // Aggiungi il punto di partenza
        mapPoints.push({
            location: routeGroup.start_location,
            sequence: 1,
            type: 'start'
        });

        // Aggiungi i punti intermedi
        routeGroup.intermediate_locations.forEach((location, index) => {
            mapPoints.push({
                location: location,
                sequence: index + 2,
                type: 'intermediate'
            });
        });

        // Aggiungi il punto di arrivo
        mapPoints.push({
            location: routeGroup.end_location,
            sequence: mapPoints.length + 1,
            type: 'end'
        });

        // Aggiungi markers per ogni punto
        const latLngs: L.LatLng[] = [];

        mapPoints.forEach((point) => {
            const icon = this.getIconForLocationType(point.location.type);
            const marker = L.marker([point.location.lat, point.location.lng], { icon })
                .addTo(this.map)
                .bindPopup(this.createLocationPopupContent(point.location, point.sequence, routeGroup));

            this.markers.push(marker);
            latLngs.push(new L.LatLng(point.location.lat, point.location.lng));
        });

        // Aggiungi linea che collega i punti
        if (latLngs.length > 1) {
            const routeLine = L.polyline(latLngs, {
                color: this.getColorForStatus(routeGroup.status),
                weight: 4,
                opacity: 0.8
            }).addTo(this.map);

            this.routeLines.push(routeLine);
        }

        // Centra la mappa sui punti della rotta
        if (latLngs.length > 0) {
            const group = new L.FeatureGroup(this.markers);
            this.map.fitBounds(group.getBounds().pad(0.1));
        }
    }

    clearMap(): void {
        // Rimuovi tutti i markers
        this.markers.forEach(marker => {
            this.map.removeLayer(marker);
        });
        this.markers = [];

        // Rimuovi tutte le linee
        this.routeLines.forEach(line => {
            this.map.removeLayer(line);
        });
        this.routeLines = [];

        // Rimuovi i marker dei magazzini se mostrati
        if (this.showingWarehouses) {
            this.hideWarehouses();
        }
    }

    toggleWarehouses(): void {
        if (this.showingWarehouses) {
            this.hideWarehouses();
        } else {
            this.showAllWarehouses();
        }
    }

    private showAllWarehouses(): void {
        const warehouses = this.getAllWarehouses();

        warehouses.forEach((warehouse, index) => {
            const marker = L.marker([warehouse.lat, warehouse.lng], {
                icon: this.warehouseIcon
            }).addTo(this.map);

            const popupContent = `
        <div class="route-popup">
          <div class="popup-header">
            <div class="sequence-number">${index + 1}</div>
            <strong>${warehouse.name}</strong>
          </div>
          <div class="popup-type">Magazzino</div>
          <div class="popup-address">${warehouse.address}</div>
          <div class="popup-info">
            <small><i class="fas fa-info-circle"></i> Magazzino operativo</small>
          </div>
        </div>
      `;

            marker.bindPopup(popupContent);
            this.warehouseMarkers.push(marker);
        });

        this.showingWarehouses = true;

        // Centra la mappa per mostrare tutti i magazzini
        if (warehouses.length > 0) {
            const group = L.featureGroup(this.warehouseMarkers);
            this.map.fitBounds(group.getBounds().pad(0.1));
        }
    }

    private hideWarehouses(): void {
        this.warehouseMarkers.forEach(marker => {
            this.map.removeLayer(marker);
        });
        this.warehouseMarkers = [];
        this.showingWarehouses = false;
    }

    private getAllWarehouses(): RoutingLocation[] {
        // Raccoglie tutti i magazzini dalle location
        const warehousePoints: RoutingLocation[] = [];
        const seenWarehouses = new Set<string>();

        this.locations.forEach(location => {
            if (location.type === 'WAREHOUSE' && !seenWarehouses.has(location.code)) {
                warehousePoints.push(location);
                seenWarehouses.add(location.code);
            }
        });

        // Aggiungi magazzini aggiuntivi se necessario
        const additionalWarehouses: RoutingLocation[] = [
            {
                code: 'WH_CENTRALE',
                name: 'Magazzino Centrale Milano',
                type: 'WAREHOUSE',
                lat: 45.4654,
                lng: 9.1859,
                address: 'Via Centrale 123, Milano'
            },
            {
                code: 'WH_ROMA_SUD',
                name: 'Magazzino Roma Sud',
                type: 'WAREHOUSE',
                lat: 41.8781,
                lng: 12.5298,
                address: 'Via Roma Sud 456, Roma'
            },
            {
                code: 'WH_TORINO',
                name: 'Magazzino Torino',
                type: 'WAREHOUSE',
                lat: 45.0703,
                lng: 7.6869,
                address: 'Corso Torino 789, Torino'
            },
            {
                code: 'WH_VENEZIA',
                name: 'Magazzino Venezia',
                type: 'WAREHOUSE',
                lat: 45.4408,
                lng: 12.3155,
                address: 'Fondamenta Venezia 321, Venezia'
            }
        ];

        // Aggiungi solo magazzini che non sono già presenti
        additionalWarehouses.forEach(warehouse => {
            if (!seenWarehouses.has(warehouse.code)) {
                warehousePoints.push(warehouse);
                seenWarehouses.add(warehouse.code);
            }
        });

        return warehousePoints;
    }

    private getIconForLocationType(type: string): L.DivIcon {
        switch (type) {
            case 'WAREHOUSE':
                return this.warehouseIcon;
            default:
                return this.warehouseIcon;
        }
    }

    private createLocationPopupContent(location: RoutingLocation, sequence: number, routeGroup: RouteGroup): string {
        return `
      <div class="route-popup">
        <div class="popup-header">
          <span class="sequence-number">${sequence}</span>
          <strong>${location.name}</strong>
        </div>
        <div class="popup-type">${this.getLocationTypeLabel(location.type)}</div>
        <div class="popup-address">${location.address}</div>
        <div class="popup-info">
          <small><i class="fas fa-route"></i> ${routeGroup.name}</small>
        </div>
      </div>
    `;
    }

    private getLocationTypeLabel(type: string): string {
        switch (type) {
            case 'WAREHOUSE':
                return 'Magazzino';
            case 'HUB':
                return 'Hub';
            case 'DEPOT':
                return 'Deposito';
            default:
                return 'Location';
        }
    }

    private getColorForStatus(status: string): string {
        switch (status) {
            case 'planned':
                return '#ffa500';
            case 'active':
                return '#4caf50';
            case 'completed':
                return '#2196f3';
            case 'cancelled':
                return '#f44336';
            default:
                return '#666';
        }
    }

    getStatusLabel(status: string): string {
        switch (status) {
            case 'planned':
                return 'Pianificata';
            case 'active':
                return 'Attiva';
            case 'completed':
                return 'Completata';
            case 'cancelled':
                return 'Cancellata';
            default:
                return status;
        }
    }

    getStatusClass(status: string): string {
        return `status-${status}`;
    }

    getStatusIcon(status: string): string {
        switch (status) {
            case 'planned': return 'fas fa-clock';
            case 'active': return 'fas fa-play-circle';
            case 'completed': return 'fas fa-check-circle';
            case 'cancelled': return 'fas fa-times-circle';
            default: return 'fas fa-question-circle';
        }
    }

    // Search and filter methods
    filterRoutes(): void {
        let filtered = [...this.originalRouteGroups];

        // Apply search filter
        if (this.searchTerm.trim()) {
            const term = this.searchTerm.toLowerCase().trim();
            filtered = filtered.filter(route => 
                route.name.toLowerCase().includes(term) ||
                route.description?.toLowerCase().includes(term) ||
                route.carrier_code?.toLowerCase().includes(term) ||
                route.transport_mode.toLowerCase().includes(term)
            );
        }

        // Apply status filter
        if (this.statusFilter) {
            filtered = filtered.filter(route => route.status === this.statusFilter);
        }

        this.filteredRouteGroups = filtered;
        this.routeGroups = filtered;
    }

    trackByRoute(index: number, route: RouteGroup): string {
        return route.routing_code;
    }

    formatDistance(distance: number): string {
        return `${distance} km`;
    }

    formatTime(minutes: number): string {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}m`;
    }

    refreshRoutes(): void {
        this.loadRoutingLegs();
    }

    createNewRoute(): void {
        // TODO: Implementa la creazione di una nuova rotta
        console.log('Creazione nuova rotta');
    }

    editRouteGroup(routeGroup: RouteGroup): void {
        // TODO: Implementa la modifica della rotta
        console.log('Modifica rotta:', routeGroup.name);
    }

    deleteRouteGroup(routeGroup: RouteGroup): void {
        // TODO: Implementa l'eliminazione della rotta
        if (confirm(`Sei sicuro di voler eliminare la rotta "${routeGroup.name}"?`)) {
            this.routeGroups = this.routeGroups.filter(r => r.routing_code !== routeGroup.routing_code);
            if (this.selectedRouteGroup?.routing_code === routeGroup.routing_code) {
                this.selectedRouteGroup = this.routeGroups[0] || null;
                if (this.selectedRouteGroup) {
                    this.displayRouteGroupOnMap(this.selectedRouteGroup);
                } else {
                    this.clearMap();
                }
            }
        }
    }

    getLocationName(locationCode: string): string {
        const location = this.locations.find(loc => loc.code === locationCode);
        return location ? location.name : locationCode;
    }

    // Leg management methods
    addNewLeg(): void {
        if (!this.selectedRouteGroup) return;

        const newLeg: RoutingLeg = {
            routing_id: Math.max(...this.selectedRouteGroup.legs.map(l => l.routing_id)) + 1,
            routing_code: this.selectedRouteGroup.routing_code,
            routing_location_start_code: 'NEW_START',
            routing_location_start_type: 'pickup',
            routing_location_end_code: 'NEW_END',
            routing_location_end_type: 'delivery',
            routing_transport_mode: 'road',
            routing_vehicle_type: 'truck',
            routing_priority_level: 1,
            routing_sequence_number: this.selectedRouteGroup.legs.length + 1,
            routing_description: `Nuova Tappa ${this.selectedRouteGroup.legs.length + 1}`,
            routing_version: 1,
            routing_created_at: new Date(),
            routing_estimated_time: 60,
            routing_distance_km: 50,
            routing_cost: 100
        };

        this.selectedRouteGroup.legs.push(newLeg);
        this.updateRouteGroupTotals(this.selectedRouteGroup);
        this.displayRouteGroupOnMap(this.selectedRouteGroup);
    }

    deleteLeg(legIndex: number): void {
        if (!this.selectedRouteGroup || legIndex < 0 || legIndex >= this.selectedRouteGroup.legs.length) return;

        const legToDelete = this.selectedRouteGroup.legs[legIndex];
        if (confirm(`Sei sicuro di voler eliminare la tappa "${legToDelete.routing_description || 'Tappa ' + legToDelete.routing_sequence_number}"?`)) {
            this.selectedRouteGroup.legs.splice(legIndex, 1);
            
            // Riordina i numeri di sequenza
            this.selectedRouteGroup.legs.forEach((leg, index) => {
                leg.routing_sequence_number = index + 1;
            });

            this.updateRouteGroupTotals(this.selectedRouteGroup);
            this.displayRouteGroupOnMap(this.selectedRouteGroup);
        }
    }

    moveLegUp(legIndex: number): void {
        if (!this.selectedRouteGroup || legIndex <= 0 || legIndex >= this.selectedRouteGroup.legs.length) return;

        const legs = this.selectedRouteGroup.legs;
        [legs[legIndex], legs[legIndex - 1]] = [legs[legIndex - 1], legs[legIndex]];
        
        // Aggiorna i numeri di sequenza
        legs.forEach((leg, index) => {
            leg.routing_sequence_number = index + 1;
        });

        this.updateRouteGroupTotals(this.selectedRouteGroup);
        this.displayRouteGroupOnMap(this.selectedRouteGroup);
    }

    moveLegDown(legIndex: number): void {
        if (!this.selectedRouteGroup || legIndex < 0 || legIndex >= this.selectedRouteGroup.legs.length - 1) return;

        const legs = this.selectedRouteGroup.legs;
        [legs[legIndex], legs[legIndex + 1]] = [legs[legIndex + 1], legs[legIndex]];
        
        // Aggiorna i numeri di sequenza
        legs.forEach((leg, index) => {
            leg.routing_sequence_number = index + 1;
        });

        this.updateRouteGroupTotals(this.selectedRouteGroup);
        this.displayRouteGroupOnMap(this.selectedRouteGroup);
    }

    clearSearch(): void {
        this.searchTerm = '';
        this.filterRoutes();
    }

    toggleLegDetails(legIndex: number): void {
        // Inizializza l'array se necessario
        if (!this.legDetailsExpanded.length && this.selectedRouteGroup) {
            this.legDetailsExpanded = new Array(this.selectedRouteGroup.legs.length).fill(false);
        }
        
        // Toggle lo stato
        this.legDetailsExpanded[legIndex] = !this.legDetailsExpanded[legIndex];
    }

    editStop(legIndex: number, type: 'start' | 'end'): void {
        if (!this.selectedRouteGroup) return;

        const leg = this.selectedRouteGroup.legs[legIndex];
        if (!leg) return;

        const locationCode = type === 'start' ? leg.routing_location_start_code : leg.routing_location_end_code;
        const currentName = this.getLocationName(locationCode);
        
        const newLocationCode = prompt(
            `Modifica la ${type === 'start' ? 'partenza' : 'destinazione'} della tappa ${legIndex + 1}:\nAttuale: ${currentName}\n\nInserisci il codice della nuova location:`,
            locationCode
        );

        if (newLocationCode && newLocationCode.trim() !== '' && newLocationCode !== locationCode) {
            if (type === 'start') {
                leg.routing_location_start_code = newLocationCode.trim();
            } else {
                leg.routing_location_end_code = newLocationCode.trim();
            }

            // Aggiorna anche il leg successivo se modifichiamo la destinazione
            if (type === 'end' && legIndex < this.selectedRouteGroup.legs.length - 1) {
                this.selectedRouteGroup.legs[legIndex + 1].routing_location_start_code = newLocationCode.trim();
            }

            // Aggiorna anche il leg precedente se modifichiamo la partenza
            if (type === 'start' && legIndex > 0) {
                this.selectedRouteGroup.legs[legIndex - 1].routing_location_end_code = newLocationCode.trim();
            }

            this.updateRouteGroupTotals(this.selectedRouteGroup);
            this.displayRouteGroupOnMap(this.selectedRouteGroup);
        }
    }

    editLeg(legIndex: number): void {
        if (!this.selectedRouteGroup || legIndex < 0 || legIndex >= this.selectedRouteGroup.legs.length) return;

        const leg = this.selectedRouteGroup.legs[legIndex];
        
        // Crea un form di modifica semplice
        const carrierCode = prompt(
            `Modifica Leg ${legIndex + 1}\n\nCodice Trasportatore:`,
            leg.routing_carrier_code || ''
        );

        if (carrierCode !== null) {
            leg.routing_carrier_code = carrierCode.trim() || undefined;

            const vehicleType = prompt(
                `Tipo Veicolo:`,
                leg.routing_vehicle_type || ''
            );

            if (vehicleType !== null) {
                leg.routing_vehicle_type = vehicleType.trim();

                const distance = prompt(
                    `Distanza (km):`,
                    leg.routing_distance_km?.toString() || ''
                );

                if (distance !== null && distance.trim() !== '') {
                    leg.routing_distance_km = parseFloat(distance);
                }

                const time = prompt(
                    `Tempo stimato (minuti):`,
                    leg.routing_estimated_time?.toString() || ''
                );

                if (time !== null && time.trim() !== '') {
                    leg.routing_estimated_time = parseFloat(time);
                }

                const cost = prompt(
                    `Costo (€):`,
                    leg.routing_cost?.toString() || ''
                );

                if (cost !== null && cost.trim() !== '') {
                    leg.routing_cost = parseFloat(cost);
                }

                this.updateRouteGroupTotals(this.selectedRouteGroup);
                this.displayRouteGroupOnMap(this.selectedRouteGroup);
            }
        }
    }

    deleteStop(legIndex: number): void {
        if (!this.selectedRouteGroup || legIndex < 0 || legIndex >= this.selectedRouteGroup.legs.length) return;

        const leg = this.selectedRouteGroup.legs[legIndex];
        const locationName = this.getLocationName(leg.routing_location_end_code);

        if (confirm(`Sei sicuro di voler eliminare la tappa finale "${locationName}"?\nQuesto eliminerà anche l'ultima tratta.`)) {
            this.deleteLeg(legIndex);
        }
    }

    private updateRouteGroupTotals(routeGroup: RouteGroup): void {
        routeGroup.totalDistance = routeGroup.legs.reduce((sum, leg) => sum + (leg.routing_distance_km || 0), 0);
        routeGroup.totalTime = routeGroup.legs.reduce((sum, leg) => sum + (leg.routing_estimated_time || 0), 0);
        routeGroup.totalCost = routeGroup.legs.reduce((sum, leg) => sum + (leg.routing_cost || 0), 0);
    }
}
